import React, { useEffect, useState } from "react";
import {
Form,
Input,
Row,
Col,
Button,
DatePicker,
Upload,
message,
Modal,
Spin,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import {
useGetSingleInvoiceRequestQuery,
useEditInvoiceRequestMutation,
useDeleteInvoiceFileRequestMutation,
} from "../../appRedux/api/partnerInvoiceApi";
import moment from "moment";
import "./style.css";

const { TextArea } = Input;
const { Dragger } = Upload;

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 6 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 18 },
    },
    };

    const AddPaymentProofModal = ({ visible, onCancel, onSuccess, invoiceId }) => {
    // RTK Query hooks
    const { data: editInvoiceRequestData, isLoading: isLoadingInvoice } = useGetSingleInvoiceRequestQuery(
        invoiceId,
        { skip: !visible || !invoiceId }
    );
    const [editInvoiceRequest] = useEditInvoiceRequestMutation();
    const [deleteInvoiceFileRequest] = useDeleteInvoiceFileRequestMutation();
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editFlag, setEditFlag] = useState(false);
    const [editedFields, setEditedFields] = useState({});
    const [filesToDelete, setFilesToDelete] = useState([]);
    const [originalFiles, setOriginalFiles] = useState([]);

    const [form] = Form.useForm();

    const handleFieldChange = (changedValues) => {
        setEditedFields((prevEditedFields) => ({
        ...prevEditedFields,
        ...changedValues,
        }));
    };

    useEffect(() => {
        if (visible && invoiceId) {
        setEditFlag(true);
        } else if (!visible) {
        // Reset state when modal is closed
        setEditFlag(false);
        setFilesToDelete([]);
        setOriginalFiles([]);
        setEditedFields({});
        }
    }, [visible, invoiceId]);

    useEffect(() => {
        if (editFlag && editInvoiceRequestData) {
        const formattedFiles = editInvoiceRequestData?.payment_file?.map((file) => ({
            uid: file.id,
            name: file.document.split("/").pop(),
            status: "done",
            url: file.document,
        })) || [];
        
        // FIX: Only set payment_date if it's not null
        const formValues = {
            proof_file: formattedFiles,
        };
        
        // Only add payment_date if it exists and is valid
        if (editInvoiceRequestData?.payment_date) {
            formValues.payment_date = moment(editInvoiceRequestData.payment_date);
        }
        
        form.setFieldsValue(formValues);
        
        // Store original files for restoration on cancel
        setOriginalFiles(formattedFiles);
        setFilesToDelete([]);
        }
    }, [editInvoiceRequestData, editFlag, form]);

    const handleRemove = (file) => {
        if (file.url) {
        // For existing files, track for deletion but don't delete immediately
        setFilesToDelete(prev => [...prev, file.uid]);
        } 
        
        // Remove from form state (both new and existing files)
        const files = form.getFieldValue('proof_file') || [];
        form.setFieldsValue({ 
            proof_file: files.filter(f => f.uid !== file.uid) 
        });
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
        return e;
        }
        return e && e.fileList;
    };

    const checkFile = (file) => {
        const allowedFileTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        ];
        const isTypeValid = allowedFileTypes.includes(file.type);
        const isSizeValid = file.size / 1024 / 1024 <= 15;

        if (!isTypeValid) {
        message.error("Please upload a PDF, JPEG, or PNG document!");
        return Upload.LIST_IGNORE;
        } else if (!isSizeValid) {
        message.error("File size must be less than or equal to 15 MB!");
        return Upload.LIST_IGNORE;
        }

        // Add file to form state
        const files = form.getFieldValue('proof_file') || [];
        form.setFieldsValue({ proof_file: [...files, file] });
        return false; // Prevent automatic upload
    };

    const handleCancel = () => {
        // Restore original files if in edit mode
        if (editFlag && originalFiles.length > 0) {
            form.setFieldsValue({
                proof_file: originalFiles,
            });
        } else {
            form.resetFields();
        }
        setEditedFields({});
        setFilesToDelete([]);
        onCancel();
    };

    const onFinish = async (values) => {
        setIsSubmitting(true);
        try {
        // Delete marked files first
        if (filesToDelete.length > 0) {
            for (const fileId of filesToDelete) {
            try {
                await deleteInvoiceFileRequest(fileId).unwrap();
            } catch (error) {
                console.error(`Failed to delete file ${fileId}:`, error);
                // Continue with other deletions even if one fails
            }
            }
        }
        
        const formData = new FormData();
        
        if (!editFlag) {
            values.proof_file.map((data) => {
            formData.append("proof_file", data.originFileObj);
            });
            formData.append("payment_date", values.payment_date.toISOString());
            formData.append("partner_status", "PAID");
        } else {
            // Always include payment_date in edit mode
            formData.append("payment_date", values.payment_date.toISOString());
            
            Object.entries(editedFields).forEach(([key, value]) => {
            if (key === "payment_date") {
                // Skip payment_date here since it's already added above
                return;
            }
            if (Array.isArray(value)) {
                const filesWithOriginFileObj = value.filter(
                (file) => file.originFileObj,
                );
                filesWithOriginFileObj.forEach((file) => {
                formData.append(`${key}`, file.originFileObj);
                });
            } else {
                formData.append(key, value.toISOString());
            }
            });
            formData.append("partner_status", "PAID");
        }
        
        await editInvoiceRequest({ id: invoiceId, data: formData }).unwrap();
        onSuccess();
        handleCancel();
        } catch (error) {
        message.error("Failed to submit payment proof");
        } finally {
        setIsSubmitting(false);
        }
    };

    return (
        <Modal
        title={null}
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        centered
        destroyOnClose
        className="add-payment-proof-modal"
        >
        <div className="add-payment-proof-header">
            <h2>{editFlag ? "Edit Payment Proof" : "Add Payment Proof"}</h2>
        </div>

        <Spin spinning={isSubmitting || isLoadingInvoice}>
            <Form
            {...formItemLayout}
            form={form}
            name="add-payment-proof"
            onValuesChange={handleFieldChange}
            onFinish={onFinish}
            // FIX: Remove initialValues or handle null case
            scrollToFirstError
            >
            <Form.Item
                label="Payment Date"
                name="payment_date"
                rules={[
                { required: true, message: "Please select a date!" },
                ]}
            >
                <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
                label="Payment Proof"
                name="proof_file"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[
                { required: true, message: "" },
                ({ getFieldValue }) => ({
                    validator(_, value) {
                    const fileList = getFieldValue("proof_file");

                    if (!fileList || fileList.length === 0) {
                        return Promise.reject(
                        new Error("Please upload a file!"),
                        );
                    }

                    const allowedFileTypes = [
                        "application/pdf",
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                    ];
                    const isFileTypeValid = fileList.every(
                        (file) =>
                        !file.originFileObj ||
                        allowedFileTypes.includes(file.type),
                    );

                    if (!isFileTypeValid) {
                        return Promise.reject(
                        new Error(
                            "Please upload a PDF, JPEG, or PNG document!",
                        ),
                        );
                    }

                    const isSizeValid = fileList.every(
                        (file) =>
                        !file.originFileObj ||
                        (allowedFileTypes.includes(file.type) &&
                            file.size / 1024 / 1024 <= 15),
                    );

                    if (!isSizeValid) {
                        return Promise.reject(
                        new Error(
                            "File size must be less than or equal to 15 MB!",
                        ),
                        );
                    }

                    return Promise.resolve();
                    },
                }),
                ]}
            >
                <Dragger
                name="proof_file"
                multiple={true}
                beforeUpload={checkFile}
                accept=".pdf, .png, .jpeg, .jpg"
                onRemove={handleRemove}
                >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                    Click or drag files to this area to upload
                </p>
                <p className="ant-upload-hint">
                    Support for a single or bulk upload (pdf, png, jpeg).
                </p>
                </Dragger>
            </Form.Item>

            <div className="modal-footer-btns">
                <Button
                className="cancel-btn"
                onClick={handleCancel}
                disabled={isSubmitting}
                >
                Cancel
                </Button>
                <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                className="submit-btn"
                >
                {editFlag ? "Save" : "Submit"}
                </Button>
            </div>
            </Form>
        </Spin>
        </Modal>
    );
};

export default AddPaymentProofModal;